import type { ReactElement } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useInputBlur } from 'packages/hooks/useInputBlur';
import { isCtrlKey } from 'packages/helpers/hotKeys';
import { framesToTime, timeToFrames } from '~/helpers/helpers';
import generateTestId from 'packages/helpers/testIdHelpers';
import { LinkableInput } from './FramePicker.styled';

type Props = {
    name: string;
    value: number;
    supportNegative?: boolean;
    frameRate: number;
    handleOnChange: (value: number) => void;
    autocorrect: (value: number) => number;
    disabled: boolean;
    tabIndex?: number;
    testId?: string;
    icon?: ReactElement | null;
    iconRight?: ReactElement | null;
    label?: string;
    id?: string;
    htmlFor?: string;
    describedby?: string;
    prefix?: string;
    linked?: boolean;
};

function formatTimeMask(input: string, supportNegative?: boolean): string {
    const isNegative = supportNegative && input.includes('-');
    const digits = input.replace(/\D/g, '').slice(0, 6);
    const padded = digits.padEnd(6, '_');
    const formatted = `${padded[0]}${padded[1]}:${padded[2]}${padded[3]}:${padded[4]}${padded[5]}`;

    return isNegative ? `-${formatted}` : formatted;
}

const FramePicker = (props: Props) => {
    const {
        name,
        value,
        supportNegative,
        frameRate,
        handleOnChange,
        autocorrect,
        disabled,
        tabIndex,
        testId,
        icon,
        iconRight,
        label,
        id,
        htmlFor,
        describedby,
        prefix,
        linked,
    } = props;
    const { onFocus, unsubscribeListener } = useInputBlur();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [frameValue, setFrameValue] = useState(framesToTime(value, frameRate));

    useEffect(() => {
        setFrameValue(framesToTime(value, frameRate));
    }, [value]);

    const handleTimeToFrames = (time) => timeToFrames(time, frameRate);
    const submit = () => {
        const currentFrame = handleTimeToFrames(inputRef?.current?.value);
        const correctFrame = autocorrect(currentFrame);
        setFrameValue(framesToTime(correctFrame, frameRate));
        handleOnChange(correctFrame);
    };

    const handleKeyDown = (e) => {
        const nEv = e.nativeEvent;

        if (isCtrlKey(nEv) && (nEv.code === 'KeyZ' || nEv.code === 'KeyY')) {
            nEv.preventDefault();
        }

        if (e.keyCode === 13) {
            submit();
        }
    };

    const onBlur = () => {
        unsubscribeListener();
        submit();
    };

    const handleChange = useCallback(
        (raw: string) => {
            const formatted = formatTimeMask(raw, supportNegative);
            setFrameValue(formatted);

            const input = inputRef.current;

            if (!input) {
                return;
            }

            // Restore cursor position: count digits before cursor in raw value,
            // then find the same digit position in the formatted value
            const cursorPos = input.selectionStart ?? raw.length;
            const digitsBefore = raw.slice(0, cursorPos).replace(/\D/g, '').length;

            let newPos = 0;
            let digitsSeen = 0;

            for (const [i, element] of [...formatted].entries()) {
                if (/\d|_/.test(element)) {
                    digitsSeen++;

                    if (digitsSeen === digitsBefore) {
                        newPos = i + 1;
                        break;
                    }
                }
            }

            // If no digits matched, place cursor at start of digit area
            if (digitsBefore === 0) {
                newPos = formatted.startsWith('-') ? 1 : 0;
            }

            requestAnimationFrame(() => {
                input.setSelectionRange(newPos, newPos);
            });
        },
        [supportNegative],
    );

    const inputTestId = generateTestId(testId);

    return (
        <LinkableInput
            value={frameValue}
            onChange={handleChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
            isDisabled={disabled}
            tabIndex={tabIndex}
            inputRef={inputRef}
            id={id}
            name={name}
            icon={icon}
            iconRight={iconRight}
            prefix={prefix}
            aria-label={label}
            aria-labelledby={htmlFor}
            aria-describedby={describedby}
            linked={linked}
            {...inputTestId}
        />
    );
};

export default FramePicker;
